import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
    Avatar, 
    Box, 
    Chip,
    Divider,
    IconButton, 
    List, 
    ListItem, 
    ListItemButton, 
    ListItemIcon, 
    ListItemText, 
    Menu, 
    Typography,
    Paper,
    Badge
} from "@mui/material";
import { 
    Check, 
    Logout, 
    PersonOutline, 
    WorkOutline,
    ExpandMoreRounded,
    AccountCircleOutlined
} from "@mui/icons-material";
import { SwitchRole } from "../../actions";
import { useMsal } from "@azure/msal-react";
import "./LogoutButton.css";

export const LogoutButton = () => {
    const roles = useSelector(state => state.userInfo.roles);
    const selectedRole = useSelector(state => state.userInfo.selectedRole);
    const { instance } = useMsal();
    const [anchorEl, setAnchorEl] = useState(null);
    const userData = useSelector(state => state.userInfo.userAuthenticated);
    const dispatch = useDispatch();

    const logout = (logoutType) => {
        if (logoutType === "popup") {
            instance.logoutPopup({
                postLogoutRedirectUri: "/",
                mainWindowRedirectUri: "/"
            });
        } else if (logoutType === "redirect") {
            instance.logoutRedirect({
                postLogoutRedirectUri: "/",
            });
        }
    }

    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const changeRole = (idRole) => {
        dispatch(SwitchRole(idRole));
        handleClose();
    }

    const handleLogout = () => {
        localStorage.clear();
        logout("popup");
    }

    const getInitials = (name) => {
        if (!name) return "U";
        return name.split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const getCurrentRoleName = () => {
        if (!roles || roles.length === 0) return "No Role";
        
        if (selectedRole === 0) {
            const mainRole = roles.find(role => role.mainRole);
            return mainRole ? mainRole.role : roles[0]?.role || "No Role";
        }
        
        const currentRole = roles.find(role => role.idRole === selectedRole);
        return currentRole ? currentRole.role : "No Role";
    };

    const isRoleSelected = (idRole, isMainRole) => {
        if (selectedRole === 0) {
            return isMainRole;
        }
        return selectedRole === idRole;
    };

    if (!userData) {
        return <></>;
    }

    return (
        <>
            <Box className="modern-user-section">
                <Box className="user-info-container">
                    <Typography variant="body2" className="user-name">
                        {userData.displayName}
                    </Typography>
                    <Chip 
                        label={getCurrentRoleName()}
                        size="small"
                        variant="outlined"
                        className="role-chip"
                        icon={<WorkOutline />}
                    />
                </Box>
                
                <IconButton
                    onClick={handleClick}
                    className="avatar-button"
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Badge 
                        overlap="circular" 
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={<ExpandMoreRounded className="dropdown-icon" />}
                    >
                        <Avatar className="user-avatar">
                            {getInitials(userData.displayName)}
                        </Avatar>
                    </Badge>
                </IconButton>
            </Box>

            <Menu
                id="account-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                className="modern-account-menu"
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                        mt: 1.5,
                        minWidth: 280,
                        borderRadius: 2,
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 20,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
            >
                {/* User Info Header */}
                <Box className="menu-user-header">
                    <Avatar className="menu-avatar">
                        {getInitials(userData.displayName)}
                    </Avatar>
                    <Box className="menu-user-details">
                        <Typography variant="subtitle1" className="menu-user-name">
                            {userData.displayName}
                        </Typography>
                        <Typography variant="body2" className="menu-user-email">
                            {userData.mail || userData.userPrincipalName}
                        </Typography>
                    </Box>
                </Box>

                <Divider />

                {/* Roles Section */}
                {roles && roles.length > 0 && (
                    <>
                        <Box className="roles-section">
                            <Typography variant="subtitle2" className="section-title">
                                <WorkOutline fontSize="small" />
                                Available Roles
                            </Typography>
                            <List dense className="roles-list">
                                {roles.map((role, index) => (
                                    <ListItem key={`role-${index}`} disablePadding>
                                        <ListItemButton 
                                            onClick={() => changeRole(role.idRole)}
                                            className={`role-item ${isRoleSelected(role.idRole, role.mainRole) ? 'selected' : ''}`}
                                        >
                                            <ListItemIcon className="role-icon">
                                                {isRoleSelected(role.idRole, role.mainRole) ? 
                                                    <Check color="primary" /> : 
                                                    <PersonOutline />
                                                }
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={role.role}
                                                secondary={role.mainRole ? "Primary Role" : "Additional Role"}
                                                primaryTypographyProps={{
                                                    variant: 'body2',
                                                    fontWeight: isRoleSelected(role.idRole, role.mainRole) ? 600 : 400
                                                }}
                                                secondaryTypographyProps={{
                                                    variant: 'caption',
                                                    color: role.mainRole ? 'primary' : 'text.secondary'
                                                }}
                                            />
                                            {role.mainRole && (
                                                <Chip 
                                                    label="Primary" 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                    className="primary-role-chip"
                                                />
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                        <Divider />
                    </>
                )}

                {/* Logout Section */}
                <ListItem disablePadding>
                    <ListItemButton onClick={handleLogout} className="logout-button">
                        <ListItemIcon>
                            <Logout color="error" />
                        </ListItemIcon>
                        <ListItemText 
                            primary="Sign Out" 
                            primaryTypographyProps={{
                                color: 'error',
                                fontWeight: 500
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            </Menu>
        </>
    );
};